import {
  HOURS_PER_DAY,
  MINUTES_PER_HOUR,
  MONTHS_PER_YEAR
} from "../../lib/constants";
import { formatCurrency, formatDays, formatNumber } from "../../lib/currency";
import type { CurrencyCode } from "../../lib/types";
import type { EcommerceInputs } from "./defaults";
import { ECOMMERCE_ASSUMPTIONS, type EcommerceResult } from "./formula";

export function ecommerceHelpHtml(
  inputs: EcommerceInputs,
  result: EcommerceResult,
  currency: CurrencyCode
): string {
  return `
    <article class="help-card">
      <h3>First Year Monthly Value Calculation</h3>
      <div class="help-equation" aria-hidden="true">
        <span class="help-term"><i class="fa-solid fa-sack-dollar"></i><small>Annual Online Revenue</small></span>
        <span class="help-op">÷</span>
        <span class="help-term"><i class="fa-solid fa-cart-shopping"></i><small>Average Order Value</small></span>
        <span class="help-op">×</span>
        <span class="help-term"><i class="fa-solid fa-triangle-exclamation"></i><small>Error Rate (halved)</small></span>
        <span class="help-op">×</span>
        <span class="help-term"><i class="fa-solid fa-money-bill-wave"></i><small>Cost per Error</small></span>
        <span class="help-op">+</span>
        <span class="help-term"><i class="fa-solid fa-user-large"></i><small>CSR Salary Pool Portion</small></span>
        <span class="help-op">−</span>
        <span class="help-term"><i class="fa-solid fa-dollar-sign"></i><small>Monthly Software + Implementation / 12</small></span>
      </div>
      <p class="help-formula">(Annual online revenue / average order value / 12) × (error rate × ${formatNumber(ECOMMERCE_ASSUMPTIONS.errorReductionFactor * 100)}%) × cost per error + (CSR monthly salary pool × ${formatNumber(ECOMMERCE_ASSUMPTIONS.csrPortion * 100)}%) - monthly software - (implementation fee / 12)</p>
      <p class="help-example">
        Example: ((${formatCurrency(result.annualOnlineRevenue, currency)} / ${formatCurrency(inputs.averageOrderValue, currency)} / ${MONTHS_PER_YEAR}) × (${formatNumber(inputs.currentErrorRatePercent)}% × ${formatNumber(ECOMMERCE_ASSUMPTIONS.errorReductionFactor * 100)}%) × ${formatCurrency(inputs.averageErrorCost, currency)}) +
        (((${formatCurrency(inputs.averageCsrSalary, currency)} × ${formatNumber(inputs.csrHeadCount)}) / ${MONTHS_PER_YEAR}) × ${formatNumber(ECOMMERCE_ASSUMPTIONS.csrPortion * 100)}%) - (${formatCurrency(inputs.monthlySoftwareAmount, currency)} + ${formatCurrency(inputs.implementationFee, currency)} / ${MONTHS_PER_YEAR})
        = <strong>${formatCurrency(result.firstYearMonthlySavings, currency)}</strong>
      </p>
    </article>
    <article class="help-card">
      <h3>Total Monthly CSR Time Saved Calculation</h3>
      <div class="help-equation" aria-hidden="true">
        <span class="help-term"><i class="fa-solid fa-cart-shopping"></i><small>Online Orders / Month</small></span>
        <span class="help-op">×</span>
        <span class="help-term"><i class="fa-solid fa-clock"></i><small>Minutes per Order</small></span>
      </div>
      <p class="help-formula">(Annual online revenue / average order value / 12) × average minutes per order</p>
      <p class="help-example">
        Example: (${formatCurrency(result.annualOnlineRevenue, currency)} / ${formatCurrency(inputs.averageOrderValue, currency)} / ${MONTHS_PER_YEAR}) × ${formatNumber(inputs.averageMinutesPerOrder)} =
        <strong>${formatNumber(result.totalMinutesSavedPerMonth)} minutes</strong> or
        <strong>${formatDays(result.totalDaysSavedPerMonth)} days</strong>
      </p>
      <p class="help-note">Days = minutes ÷ ${MINUTES_PER_HOUR} ÷ ${HOURS_PER_DAY}.</p>
    </article>
  `;
}
